import { Users } from "lucide-react";
import { type QuizConfig } from "~/types/quiz";

const LIKERT_5 = [
  "Uopšte se ne slažem",
  "Uglavnom se ne slažem",
  "Nisam siguran/na",
  "Uglavnom se slažem",
  "Potpuno se slažem",
];

// 16 Personality Types quiz based on Jungian dimensions
// Questions cycle: E/I → S/N → T/F → J/P (repeating 4x = 16 questions)
// Higher score = more E, N, F, P
export const testLicnosti: QuizConfig = {
  id: "test-licnosti",
  illustration: {
    src: "/images/quizzes/test-licnosti.webp",
    alt: "Žena zastaje između druženja u dvorištu i mirnog kutka za čitanje.",
  },
  lastModified: "2026-07-31",
  title: "Test ličnosti: Koji si od 16 tipova ličnosti?",
  shortTitle: "Test ličnosti",
  description:
    "Besplatan test ličnosti sa 17 pitanja koji otkriva tvoj tip ličnosti. Zasnovan na Jungovoj teoriji, rezultat dobijaš za 5 minuta.",
  longDescription:
    "Test ličnosti zasnovan na četiri dimenzije Jungove teorije: introvertnost ili ekstrovertnost, opažanje ili intuicija, mišljenje ili osećanje, prosuđivanje ili percepcija. Sa 17 pitanja i AI analizom, dobijaš personalizovan profil koji otkriva kako funkcionišeš u odnosima, poslu i svakodnevnom životu.",
  icon: Users,
  duration: "5 min",
  completions: 0,
  category: "Ličnost i emocije",
  focusAreasWanted: [
    "self_development",
    "identity",
    "relationships",
    "decision_making",
  ],
  featured: true,
  promptExtension: `
    ## CRITICAL: This is a 16 Personality Types quiz (Jungian typology)

    The scores represent 4 dimensions on a 0-100 scale:
    - extraversion_introversion: <=50 = Introvertnost (I), >50 = Ekstrovertnost (E)
    - sensing_intuition: <=50 = Opažanje (S), >50 = Intuicija (N)
    - thinking_feeling: <=50 = Mišljenje (T), >50 = Osećanje (F)
    - judging_perceiving: <=50 = Prosuđivanje (J), >50 = Percepcija (P)

    The quiz type field already contains the 4-letter code (e.g., "16 Personality Types (INFP)").

    ## How to structure the response:

    **tagline**: State the user's type clearly, e.g. "Tvoj tip ličnosti je INFP: Idealistički posrednik"

    **message**: Structure as:
    1. **Type overview** (1 paragraph): Name the type, describe what makes this type unique. Use the format "Tvoj tip ličnosti je [CODE]: [Name]" followed by a vivid description of their core traits.
    2. **Dimension breakdown** (4 short paragraphs): For each dimension, explain WHERE they fall and what it means practically. Show the score as percentage, e.g. "Na skali introvertnosti/ekstrovertnosti, tvoj rezultat je 35/100, što znači da si umereni introvert."
    3. **Strengths & blind spots** (1-2 paragraphs): What this type does well, what to watch out for. Give concrete daily-life examples.
    4. **Relationships & work** (1 paragraph): How this type functions in key life areas.

    **advice**: Give type-specific growth tips. Not generic. Examples:
    - For introverts: "Pre važnog sastanka, uzmi 10 minuta za sebe. To nije izbegavanje, to je punjenje baterija."
    - For Feeling types: "Kada donosiš odluku, napravi listu 'za' i 'protiv' pre nego što se osloniš na osećaj. Kombinacija logike i intuicije daje najbolje rezultate."

    **score**: Use the extraversion_introversion score as the primary score (since that's the most prominent dimension).

    **isInNeedForTherapy**: false (this is a trait quiz, not clinical)

    Use emoji headers for each dimension section (e.g., 🔋 Energija, 🔍 Informacije, 💡 Odluke, 📋 Stil života).
  `,
  questions: [
    // === Round 1 ===
    // Q1: E/I
    {
      type: "radio",
      question:
        "Na okupljanjima sa puno ljudi obično se osećam puno energije i uzbuđenja.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q2: S/N
    {
      type: "radio",
      question:
        "Više me privlače apstraktne ideje i mogućnosti nego konkretni detalji i činjenice.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q3: T/F
    {
      type: "radio",
      question:
        "Kada prijatelj traži savet, prvo pitam kako se oseća, a tek onda analiziram opcije.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q4: J/P
    {
      type: "radio",
      question:
        "Kada imam slobodan dan, preferiram da nemam plan i da odlučujem u momentu.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // === Round 2 ===
    // Q5: E/I
    {
      type: "radio",
      question:
        "Posle napornog dana, energiju punim tako što se vidim sa prijateljima.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q6: S/N
    {
      type: "radio",
      question:
        "Više volim da razmišljam o budućim mogućnostima nego o onome što se dešava ovde i sada.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q7: T/F
    {
      type: "radio",
      question:
        "Važnije mi je da odluka bude u skladu sa mojim vrednostima nego da bude strogo logična.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q8: J/P
    {
      type: "radio",
      question:
        "Moj radni prostor izgleda malo haotično, ali ja znam gde je šta.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // === Round 3 ===
    // Q9: E/I
    {
      type: "radio",
      question:
        "Kada upoznam nove ljude, lako pokrećem razgovor i brzo se uklopim.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q10: S/N
    {
      type: "radio",
      question:
        "Kada rešavam problem, oslanjam se na intuiciju i veliku sliku umesto na proveren pristup korak po korak.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q11: T/F
    {
      type: "radio",
      question:
        "Kada neko u timu napravi grešku, prvo proverim da li je ta osoba dobro, pa tek onda pričamo o grešci.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q12: J/P
    {
      type: "radio",
      question:
        "Rokovi i čvrsti planovi me ograničavaju, najkreativniji/a sam kada imam slobodu.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // === Round 4 ===
    // Q13: E/I
    {
      type: "radio",
      question:
        "U poslu ili studijama, preferiram timski rad i razmenu ideja umesto samostalnog rada.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q14: S/N
    {
      type: "radio",
      question:
        "Kada planiram budućnost, vodim se vizijom i velikim snovima, detalji dolaze kasnije.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q15: T/F
    {
      type: "radio",
      question:
        "Gledajući emotivnu scenu u filmu, lako se uživim i ponekad mi i oči zasuzne.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    // Q16: J/P
    {
      type: "radio",
      question:
        "Kada se planovi iznenada promene, to me obraduje jer volim spontanost i iznenađenja.",
      options: LIKERT_5,
      scores: [1, 2, 3, 4, 5],
    },
    {
      type: "textarea",
      question:
        "Opiši situaciju u kojoj si najviše prepoznao/la svoj tip ličnosti:",
    },
  ],
  copy: {
    title: "Tvoj tip ličnosti",
    subtitle: "Otkrij koji si od 16 tipova ličnosti",
    scoreLabel: "Tvoji rezultati po dimenzijama:",
    adviceTitle: "Saveti za tvoj tip",
    resourcesTitle: "Korisni resursi",
    whatNextTitle: "Šta dalje?",
    shareButtonText: "Podeli rezultat",
  },
  landing: {
    summary:
      "Ovaj test ličnosti otkriva tvoj tip na osnovu četiri ključne dimenzije: kako puniš energiju (introvertnost ili ekstrovertnost), kako obrađuješ informacije (opažanje ili intuicija), kako donosiš odluke (mišljenje ili osećanje) i kako organizuješ život (prosuđivanje ili percepcija). Sa 17 pitanja i AI analizom, dobijaš detaljan profil sa konkretnim uvidima o tome kako funkcionišeš u odnosima, poslu i svakodnevnom životu.",
    highlights: [
      {
        title: "17 pitanja, 4 dimenzije",
        description:
          "Brz i precizan test koji meri četiri ključne dimenzije tvoje ličnosti.",
      },
      {
        title: "Zasnovan na Jungovoj teoriji",
        description:
          "Model sa decenijama istraživanja, koristi se u psihologiji i HR-u širom sveta.",
      },
      {
        title: "AI personalizovani rezultat",
        description:
          "Veštačka inteligencija analizira tvoje odgovore i daje konkretne, praktične savete.",
      },
    ],
    whoIsItFor:
      "Za svakoga ko želi da brzo sazna svoj tip ličnosti. Posebno koristan ako biraš karijeru, želiš da bolje razumeš sebe u odnosima ili te zanima zašto na stvari reaguješ baš onako kako reaguješ.",
    whatYouLearn:
      "Saznaćeš da li si više introvert ili ekstrovert, da li se oslanjaš na intuiciju ili činjenice, da li odluke donosiš glavom ili srcem i da li voliš strukturu ili spontanost. Svaka dimenzija dolazi sa praktičnim objašnjenjem i savetima.",
    howItWorks:
      "Test sadrži 16 tvrdnji na koje odgovaraš na skali od 1 do 5. Na osnovu tvog obrasca odgovora, AI određuje tvoj tip ličnosti i generiše personalizovani profil sa uvidima i savetima.",
    nextSteps:
      "Nakon testa, možeš da uporediš rezultat sa prijateljima ili partnerom. Ako želiš da dublje razumeš svoju ličnost i kako ona utiče na tvoje odnose, karijeru ili svakodnevne odluke, razgovor sa terapeutom može biti odličan sledeći korak.",
    faq: [
      {
        question: "Šta je test ličnosti sa 16 tipova?",
        answer:
          "Test ličnosti sa 16 tipova je zasnovan na teoriji Karla Junga i meri četiri dimenzije: introvertnost/ekstrovertnost, opažanje/intuiciju, mišljenje/osećanje i prosuđivanje/percepciju. Kombinacija ove četiri dimenzije daje jedan od 16 mogućih tipova ličnosti, svaki sa jedinstvenim karakteristikama.",
      },
      {
        question: "Koliko je ovaj test pouzdan?",
        answer:
          "Jungova tipologija je jedan od najšire korišćenih modela u psihologiji ličnosti. Ovaj test je dizajniran za samoprocenu i daje korisne uvide, ali nije zamena za profesionalnu procenu. Za najrelevantnije rezultate, odgovaraj iskreno, bez razmišljanja o tome šta je 'pravi' odgovor.",
      },
      {
        question: "Koja je razlika između ovog testa i Big 5?",
        answer:
          "Big 5 meri pet dimenzija ličnosti na skali (npr. 'visoka ekstraverzija'), dok ovaj test svrstava u jedan od 16 tipova (npr. INFP). Big 5 je precizniji za naučna istraživanja, ali tipovi ličnosti su intuitivniji i lakše zapamtljivi. Idealno je uraditi oba testa za potpuniju sliku.",
      },
      {
        question: "Da li se tip ličnosti menja tokom života?",
        answer:
          "Osnovne preference ostaju relativno stabilne, ali se mogu pomalo menjati sa životnim iskustvima. Na primer, introvert može naučiti da se bolje snalazi u društvu, ali verovatno će mu i dalje trebati vreme za sebe da se oporavi. Preporučujemo ponavljanje testa jednom godišnje.",
      },
      {
        question: "Da li ovaj test zamenjuje razgovor sa psihologom?",
        answer:
          "Ne. Test je korisna polazna tačka za samoupoznavanje, ali ne zamenjuje profesionalnu procenu. Ako imaš pitanja o svojoj ličnosti ili želiš da radiš na specifičnim temama, razgovor sa licenciranim terapeutom je uvek dobra ideja.",
      },
    ],
    ctaLabel: "Započni test ličnosti",
  },
};
